import { Button } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { BillingRuleEntity } from "../../../../../entities/ServicesEntities/BillingEntities/BillingRuleEntity";
import { BillingRuleRateEntity } from "../../../../../entities/ServicesEntities/BillingEntities/BillingRuleRateEntity";
import { BillingRuleToUserTypeEntity } from "../../../../../entities/ServicesEntities/BillingEntities/BillingRuleToUserTypeEntity";
import { CreateBillingRule } from "../../../../../entities/ServicesEntities/BillingEntities/CreateBillingRule";
import { UpdateBillingRule } from "../../../../../entities/ServicesEntities/BillingEntities/UpdateBillingRule";
import { ADD_BILLING_RULE_FORM_STEPS, BILLING_RULE_TYPES } from "../../../../../enums/Enums";
import Icons from "../../../../../resources/icons/Icons";
import Strings from "../../../../../resources/Strings";
import { getDateFromTimeString } from "../../../../../utils/CommonUtils";
import EllipseButton from "../../../Buttons/EllipseButton/EllipseButton";
import AddBillingRuleStep from "../Steps/AddBillingRuleStep";
import AddRateStep from "../Steps/AddRateStep";
import classes from "../WizardForm.module.css";
import * as biliingActionsService from "../../../../../services/ActionsService/BillingActionsService";
import store from "../../../../../redux/Store";
import { commonActions } from "../../../../../redux/reducers/CommonReducer";
import { billingActions } from "../../../../../redux/reducers/BillingReducer";
import { 
  getCalculateDays,
  getRuleDurationComparisonWithRates,
  getStartTimeFromDate,
  isStartTimeBigger,
  getDaysSelectionArrayFromDaysInt, 
  getSortedRates,
  isMidnightToMidnight} 
from "../../../../../utils/BillingUtils";
import { isValidDate } from "../../../../../utils/FormUtils";
import { showBillingRemainingTimeError, showErrorMessage, showUpdateSuccessMessage, showAddBillingRuleSuccessMessage } from "../../../../../utils/MessagesUtils";

const AddBillingRuleForm = (props) => {

  // scope change
  const selectedSites = useSelector((state) => state.user.selectedSites);
  const [selectedSite, setSelectedSite] = useState((state)=> props.isEditMode ? props.site : selectedSites?.length >  0 ? selectedSites[0] : null );

  const [formData, setFormData] = useState({
    ruleId: props?.isEditMode ? props.id : 0,
    billingRuleToUserTypes : props?.isEditMode ? props.billingRuleToUserTypes : [], // added
    userType: props?.isEditMode ? props.billingRuleToUserTypes.map(br => br.userTypeId) : [],
    ruleName: props?.isEditMode ? props.ruleName : "",
    isFullDay: props?.isEditMode ? isMidnightToMidnight(props.startTime, props.endTime) : false, 
    days: props?.isEditMode ? getDaysSelectionArrayFromDaysInt(props.days) : [],
    startTime: props?.isEditMode ? getDateFromTimeString(props.startTime) : "",
    endTime: props?.isEditMode ? getDateFromTimeString(props.endTime) : "",
    rates: props?.isEditMode ? (props.billingRuleRates ? getSortedRates(props.billingRuleRates) : []) : []
  });

  useEffect(() => {
    if (props.isEditMode) {
      isRuleFilled(0);
    }
  }, []);

  // added
  const addRateStepRef = useRef();

  // added
  const [isRuleDurationFilled, setIsRuleDurationFilled] = useState(false);
  
  const [stateData, setStateData] = useState({
    currentStep: ADD_BILLING_RULE_FORM_STEPS.ADD_BILLING_RULE,
    totalSteps: 2,
    isCurrentStepValid: false,
  });

  const handleDeleteRate = (id) => {
    let indexToDelete = formData.rates.findIndex(r => r.id === id);
    let ratesCopy = [...formData.rates];
    ratesCopy.splice(indexToDelete, 1);
    setIsRuleDurationFilled(false);
    setFormData({...formData, ["rates"]: ratesCopy});
  };

  const handleTimeChange = (newProp) => {
    let startTime = formData?.startTime === '' ? '' : formData?.startTime;
    let endTime = formData?.endTime === '' ? '' : formData?.endTime;
    let startTimeDate;
    let endTimeDate;
    let isFullDay = formData?.isFullDay; // full day (midnight to midnight)
    let totalDurationMinutes;

    if (Object.keys(newProp)[0] === 'startTime' && Object.values(newProp)[0] !== startTime) {
      startTime = Object.values(newProp)[0];
    }

    if (Object.keys(newProp)[0] === 'endTime' && Object.values(newProp)[0] !== endTime) {
      endTime = Object.values(newProp)[0];
    }

    startTimeDate = getStartTimeFromDate(startTime)//new Date(startTime)?.toLocaleTimeString('en-US', { hour12: false  });
    endTimeDate = new Date(endTime)?.toLocaleTimeString('en-US', { hour12: false  });

    if (startTime !== '' && endTime !== '') {
      isFullDay = isMidnightToMidnight(startTimeDate, endTimeDate);
      totalDurationMinutes = getRuleDurationComparisonWithRates(startTime, endTime, formData?.rates, isFullDay);
    }

    if (isStartTimeBigger(startTimeDate, endTimeDate) || startTimeDate === endTimeDate && !isFullDay ||
        !isValidDate(startTime) || !isValidDate(endTime) || 
        totalDurationMinutes % 30 !== 0) {
      // disable button
      setIsRuleDurationFilled(true); 
      // clear rates and set formData
      setFormData({...formData, ["rates"]: [], ["isFullDay"] : isFullDay, ...newProp});

    } else if (totalDurationMinutes <= 0) { 
      setIsRuleDurationFilled(false); 
      setFormData({...formData, ["rates"]: [], ["isFullDay"] : isFullDay, ...newProp});
    } else {
      // enable button
      setIsRuleDurationFilled(false);
      // set formData
      setFormData({...formData, ["isFullDay"] : isFullDay, ...newProp});
      //handleChange(newProp);
    }
  };

  const handleChange = (newProp) => {
    setFormData({ ...formData, ...newProp });
  };

  const handleCurrentStepValidity = (hasError) => {
    setStateData({ ...stateData, isCurrentStepValid: !hasError });
  };

  const updateBillingRule = () => {
    const { ruleId, ruleName, userType, days, startTime, endTime, rates } = formData;

    const billingUserTypes = userType.map(
      (userTypeId) => new BillingRuleToUserTypeEntity(0, ruleId, userTypeId)
    );  

    const ratesEntity = buildBillingRates(rates);

    const ParkingRuleEntity = new UpdateBillingRule(
      new BillingRuleEntity(
        ruleId,
        ruleName,
        selectedSite?.id,
        BILLING_RULE_TYPES.PARKING_RULE,
        billingUserTypes,
        ratesEntity,
        getCalculateDays(days),
        startTime.toTimeString()?.slice(0,8),
        endTime.toTimeString()?.slice(0,8),
      )
    );

    biliingActionsService.updateBillingRule(ParkingRuleEntity).then((response) => {
      store.dispatch(commonActions.closeSideModal());
      if (response.data.isSuccess) {
        store.dispatch(billingActions.setBillingRules(response.data.entity));
        // show message on update
        showUpdateSuccessMessage();
      } else {
        showErrorMessage(response.data?.error);
      }
    });
  };

  const addBillingRule = () => {
    const { ruleId, ruleName, userType, days, startTime, endTime, rates } = formData;

    const billingUserTypes = userType.map(
      (userTypeId) => new BillingRuleToUserTypeEntity(0, ruleId, userTypeId)
    );

    const ratesEntity = buildBillingRates(rates);

    const ParkingRuleEntity = new CreateBillingRule(
      new BillingRuleEntity(
        0,
        ruleName,
        selectedSite?.id,
        BILLING_RULE_TYPES.PARKING_RULE,
        billingUserTypes,
        ratesEntity,
        getCalculateDays(days),
        startTime.toTimeString()?.slice(0,8),
        endTime.toTimeString()?.slice(0,8),
      )
    );

    biliingActionsService.addBillingRule(ParkingRuleEntity).then((response) => {
      store.dispatch(commonActions.closeSideModal());
      if (response.data.isSuccess) {
        store.dispatch(billingActions.setBillingRules(response.data.entity));
        // show message on create
        showAddBillingRuleSuccessMessage();
      } else {
        showErrorMessage(response.data?.error);
      }
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // cant create rule if there is still remaining time to fill with rates
    if (isValidRuleTimeDuration() === true) {
      showBillingRemainingTimeError();
      return;
    }

    setFormData({ ...formData, ["isStepSubmitted"]: true });
    if (stateData?.isCurrentStepValid) {
      if (props?.isEditMode) {
        updateBillingRule();
      } else {
        addBillingRule();
      }
    }
  };

  const buildBillingRates = (rates) =>{
    return rates.map((rate, index)=>{
      if (props.isEditMode) {
        // if rate already associated to one of the billing rule id, it is not a generated one while on edit mode
        return new BillingRuleRateEntity(rate?.billingRuleId !== undefined ? parseInt(rate.id) : 0, parseFloat(rate.rateAmount), rate?.chargeBy, rate?.duration, rate.period, index+1, formData.ruleId);
      }
      else {
        return new BillingRuleRateEntity(0, parseFloat(rate.rateAmount), rate?.chargeBy, rate?.duration, rate.period, index+1, 0);
      }
    })
  }

  // Handles the show / hide the Add new Rate button
  const isRuleFilled = (selectedDurationInMinutes) => {
    let totalDurationMinutesAfterSelection = getRuleDurationComparisonWithRates(formData?.startTime, formData?.endTime, formData?.rates, formData?.isFullDay) - selectedDurationInMinutes;
    if (totalDurationMinutesAfterSelection <= 0) {
      setIsRuleDurationFilled(true); 
      return true;    
    }
    else {
      setIsRuleDurationFilled(false);
      return false;
    }
  };

  const clearRates = () => {
    setFormData({...formData, ["rates"]: []});
  };

  const isValidRuleTimeDuration = () => {
    // check if fields are empty
    if (formData?.startTime === "" || formData?.endTime === "") {
      return false;
    }

    let startTimeDate = getStartTimeFromDate(formData?.startTime)
    let endTimeDate = new Date(formData?.endTime)?.toLocaleTimeString('en-US', { hour12: false  });

     // check if start time after end time
     if (isStartTimeBigger(startTimeDate, endTimeDate)) {
      return false;
     }

    //  if (formData?.startTime > formData?.endTime) {
    //   clearRates();
    //   return false;
    //  }
    let totalDurationMinutes = getRuleDurationComparisonWithRates(formData?.startTime, formData?.endTime, formData?.rates, formData?.isFullDay);
    if (totalDurationMinutes <= 0) {
      return false;
    }
    
    return true;
  }

  const next = () => {
    // validate rule time duration
    if (isValidRuleTimeDuration() === false) {
      return;
    }

    let currentStep = stateData.currentStep;
    currentStep =
      currentStep >= stateData.totalSteps - 1
        ? stateData.totalSteps
        : currentStep + 1;
    setStateData({ ...stateData, currentStep: currentStep });
    setFormData({ ...formData, ["isStepSubmitted"]: false });
    addRateStepRef.current.setPeriodNumber(0);
    addRateStepRef.current.resetRateFields();
  };

  const prev = () => {
    let currentStep = stateData.currentStep;
    currentStep = currentStep <= 1 ? 1 : currentStep - 1;
    setStateData({ ...stateData, currentStep: currentStep });
  };

  return (
    <>
      {stateData.currentStep !== 1 && (
        <div className={classes.prev}>
          <img src={Icons.LEFT_ARROW} />
          <Button onClick={prev}>Back</Button>
        </div>
      )}
      <form name="AddBillingRule" onSubmit={handleSubmit}>
        <div className={classes.formContainer}>
          <AddBillingRuleStep
            currentStep={stateData.currentStep}
            handleChange={handleChange}
            handleCurrentStepValidity={handleCurrentStepValidity}
            handleDeleteRate={handleDeleteRate}
            handleTimeChange={handleTimeChange}
            isRuleDurationFilled={isRuleDurationFilled}
            next={next}
            selectedSite={selectedSite}
            setSelectedSite={setSelectedSite}
            isEditMode={props?.isEditMode}
            {...formData}
          />
          <AddRateStep
            ref={addRateStepRef}
            currentStep={stateData.currentStep}
            isCurrentStepValid={stateData.isCurrentStepValid}
            handleChange={handleChange}
            handleCurrentStepValidity={handleCurrentStepValidity}
            isRuleFilled={isRuleFilled}
            prev={prev}
            isEditMode={props?.isEditMode}
            selectedSite={selectedSite}
            {...formData}
          />
        </div>
        {stateData.currentStep ==
          ADD_BILLING_RULE_FORM_STEPS.ADD_BILLING_RULE && (
          <div className={classes.next}>
            <EllipseButton type="submit">{props?.isEditMode ? Strings.SAVE_CHANGES : Strings.CREATE_RULE}</EllipseButton>
          </div>
        )}
      </form>
    </>
  );
};

export default AddBillingRuleForm;
